import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { cloneDeep } from 'lodash';
import { BaseModal } from 'components/UI';
import { SelectableList } from 'components/UI';
import { Box, Grid, Button } from '@material-ui/core';
import { vehicleActions } from 'state/modules/vehicles';
import { VehiclesGroupsActions } from 'state/modules/vehiclesGroups';
import { useStyles } from './BulkShareServicesModal.styled';

const BulkShareServicesModal = ({ vehicleIds, open, vehicleGroupId, handleClick }) => {
  const dispatch = useDispatch();
  const classes = useStyles();
  const [shared, setShared] = useState(false);
  const [assignedServices, setAssignedServices] = useState([]);
  const [availablesServices, setAvailablesServices] = useState([]);
  const [isAvailableSelected, setAvailableSelected] = useState(false);
  const [selectedServiceRow, setSelectedAvailablesRow] = useState(null);
  const { fetchingUpdateSharing, fetchedUpdateSharing } = useSelector((state) => state.vehicles);
  const { availableServices, availableServicesPagination, fetchAvailableServices } = useSelector(
    (state) => state.vehiclesGroups
  );
  const sharingType = useSelector(
    (state) => state.config?.settings?.core?.partnership?.sharing_type
  );

  const [paginationParams, setPaginationParams] = useState({
    page: 1,
    limit: 10,
  });

  const availablesServicesColumns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      sortable: false,
    },
  ];

  const assignedServicesColumns = [
    {
      name: i18n.t('common.name'),
      selector: 'name',
      sortable: false,
    },
  ];

  const onAvailablesRowClicked = (row) => {
    setSelectedAvailablesRow(row?.id);
  };

  /**
   * Function to be executed after form validation
   * @param {object} values
   */
  const onShareVehicle = () => {
    setShared(true);
    const payload = {
      organization_id: selectedServiceRow,
      vehicles_ids: vehicleIds,
    };
    dispatch(vehicleActions.fetchBulkSharing(payload));
  };

  useEffect(() => {
    if (fetchedUpdateSharing && selectedServiceRow && shared) {
      setShared(false);
      dispatch(vehicleActions.resetUpdateSharing());
      const orgId = selectedServiceRow;
      const _org = availableServices.find((el) => el.id === orgId);
      const _services = availablesServices.map((val) => {
        if (val.id === selectedServiceRow) {
          val.disabled = true;
          val.selected = false;
        }
        return val;
      });
      const assignedServicesArr = [...assignedServices, { ..._org, selected: false }];
      setAssignedServices(assignedServicesArr);
      setAvailablesServices(_services);
      setSelectedAvailablesRow(null);
    }
  }, [fetchedUpdateSharing, selectedServiceRow, shared, dispatch]);

  useEffect(() => {
    if (vehicleGroupId) {
      dispatch(
        VehiclesGroupsActions.fetchAvailableServices({
          vehicle_group_id: vehicleGroupId,
          status: 'ACTIVE',
          ...paginationParams,
        })
      );
    }
  }, [vehicleGroupId, dispatch, paginationParams]);

  useEffect(() => {
    if (availableServices?.length > 0) {
      setAvailablesServices(cloneDeep(availableServices));
    }
  }, [availableServices]);

  const handlePageChange = (page) => setPaginationParams({ page, limit: paginationParams.limit });
  const handlePerRowsChange = (limit) =>
    setPaginationParams({ page: paginationParams.page, limit });

  return (
    <BaseModal
      open={open}
      maxWidth={1000}
      enableCloseButton={true}
      handleClick={handleClick}
      loading={fetchingUpdateSharing}
      title={
        sharingType === 'FFB'
          ? i18n.t('vehicle.update_sharing_title_partners')
          : i18n.t('vehicle.update_sharing_title')
      }
    >
      <Grid item xs={12}>
        <Grid container direction="row" justifyContent="space-around" alignItems="flex-start">
          <Grid item xs={5}>
            <SelectableList
              mainTable={true}
              data={availablesServices || []}
              columns={availablesServicesColumns}
              onRowClicked={(row) => {
                setAvailableSelected(!!row);
                onAvailablesRowClicked(row);
              }}
              handlePageChange={handlePageChange}
              handlePerRowsChange={handlePerRowsChange}
              title={
                sharingType === 'FFB'
                  ? i18n.t('vehicle.available_partners')
                  : i18n.t('vehicle.available_services')
              }
              loading={fetchAvailableServices === 'LOADING'}
              totalRows={availableServicesPagination?.totalRows || 1}
              paginationDefaultPage={availableServicesPagination?.page || 1}
            />
          </Grid>
          <Grid item xs={2}>
            <Box display="flex" alignItems="center" flexDirection="column" justifyContent="center">
              <Box py={1} px={2} style={{ width: '100%' }}>
                <Button
                  fullWidth
                  color="primary"
                  variant="outlined"
                  data-cy="add-service"
                  onClick={onShareVehicle}
                  disabled={fetchingUpdateSharing || !isAvailableSelected}
                >
                  {i18n.t('common.add')}
                </Button>
              </Box>
            </Box>
          </Grid>
          <Grid item xs={5}>
            <Box mb={5}>
              <SelectableList
                disableSelectable
                pagination={false}
                data={assignedServices || []}
                loading={fetchingUpdateSharing}
                columns={assignedServicesColumns}
                title={
                  sharingType === 'FFB'
                    ? i18n.t('vehicle.selected_partners')
                    : i18n.t('vehicle.selected_services')
                }
              />
            </Box>
          </Grid>
        </Grid>
      </Grid>
    </BaseModal>
  );
};

BulkShareServicesModal.propTypes = {
  open: PropTypes.bool.isRequired,
  handleClick: PropTypes.func.isRequired,
};

BulkShareServicesModal.defaultProps = {
  open: false,
  handleClick: () => {},
};

export default BulkShareServicesModal;
